"use client";

/**
 * VoiceTranscriptCard — renders a voice-note transcript in a styled card.
 *
 * When a user sends a voice note, the transcript is extracted from the
 * ``<voice_transcript>`` tag in the user message by
 * ``extractVoiceTranscript()`` in ``render-items.ts``.  This component
 * renders the transcript text in a card with a microphone icon so the
 * user can see at a glance that the message was a voice note (not typed
 * text) and read what was transcribed.
 *
 * The card is intentionally compact — it sits above the message text and
 * file attachments in the user message bubble.  The transcript text is
 * rendered as plain text with preserved whitespace (``pre-line``) so
 * multi-line transcripts display naturally.
 */

import { MicIcon } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Card } from "@/components/ui/card";
import { cn } from "@/lib/utils";

export interface VoiceTranscriptCardProps {
  /** The transcribed text from the voice note. */
  transcript: string;
  /** Optional className for layout adjustments. */
  className?: string;
}

export function VoiceTranscriptCard({
  transcript,
  className,
}: VoiceTranscriptCardProps) {
  const { t } = useTranslation();

  return (
    <Card
      className={cn("flex gap-2 p-3 text-sm", className)}
      data-testid="voice-transcript-card"
    >
      <MicIcon className="mt-0.5 size-4 shrink-0 text-primary" />
      <div className="flex flex-col gap-1">
        <span className="text-xs font-medium text-muted-foreground">
          {t("voice.transcript", { defaultValue: "Voice note transcript" })}
        </span>
        <span className="whitespace-pre-line break-words text-foreground">
          {transcript}
        </span>
      </div>
    </Card>
  );
}
